/**
 * TestimonialsSection - Client testimonials showcase
 * Features: Avatar, Rating, responsive grid, verified badge, company info
 */
import { Container, Grid, Box, Paper, Typography, Avatar, Rating, Chip, Link } from "@mui/material";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { getTestimonials } from "../constants/testimonials";
import VerifiedIcon from "@mui/icons-material/Verified";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import BusinessIcon from "@mui/icons-material/Business";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const TestimonialsSection = () => {
  const { t } = useTranslation();
  const testimonials = getTestimonials(t);

  return (
    <Container sx={{ py: 6 }}>
      <Typography
        variant="overline"
        align="center"
        display="block"
        sx={{ color: "primary.main", fontWeight: 700, letterSpacing: "0.15em", mb: 1 }}
      >
        {t("home.testimonials.eyebrow")}
      </Typography>
      <Typography
        variant="h4"
        align="center"
        gutterBottom
        sx={{
          fontWeight: 700,
          fontSize: { xs: "1.8rem", md: "2.2rem" },
          mb: 4,
          color: "text.primary",
        }}
      >
        {t("home.testimonials.title")}
      </Typography>
      <Grid container spacing={4} justifyContent="center">
        {testimonials.map((testimonial, index) => (
          <Grid item xs={12} md={6} lg={4} key={index}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.15, duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  borderRadius: 2,
                  borderLeft: "4px solid",
                  borderColor: "primary.main",
                  transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    borderColor: "secondary.main",
                    boxShadow: (theme) =>
                      `0 16px 40px ${theme.palette.primary.main}22`,
                  },
                }}
              >
                {/* Quote Icon */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    opacity: 0.07,
                    color: "primary.main",
                  }}
                >
                  <FormatQuoteIcon sx={{ fontSize: 64 }} />
                </Box>

                {/* Header with Avatar and Info */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 2, position: "relative", zIndex: 1 }}>
                  <Avatar
                    src={testimonial.companyLogo || undefined}
                    sx={{
                      background: (theme) => theme.gradients.primary,
                      width: 52,
                      height: 52,
                      mr: 2,
                      fontSize: "1.3rem",
                      fontWeight: 700,
                      boxShadow: (theme) =>
                        `0 4px 12px ${theme.palette.primary.main}44`,
                      "& img": { objectFit: "contain", p: "6px" },
                    }}
                    aria-label={`Avatar de ${testimonial.name}`}
                  >
                    {!testimonial.companyLogo && testimonial.avatar}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
                        {testimonial.name}
                      </Typography>
                      {testimonial.verified && (
                        <VerifiedIcon sx={{ fontSize: 18, color: "primary.main" }} />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.875rem" }}>
                      {testimonial.role}
                    </Typography>
                  </Box>
                </Box>

                {/* Rating */}
                <Rating
                  value={testimonial.rating}
                  readOnly
                  size="small"
                  sx={{
                    mb: 2,
                    "& .MuiRating-iconFilled": { color: "secondary.main" },
                  }}
                  aria-label={`${testimonial.rating} de 5 estrellas`}
                />

                {/* Comment */}
                <Typography
                  variant="body2"
                  sx={{
                    fontStyle: "italic",
                    flex: 1,
                    mb: 2,
                    lineHeight: 1.6,
                    color: "text.primary",
                  }}
                >
                  "{testimonial.comment}"
                </Typography>

                {/* Company Info */}
                {testimonial.company && (
                  <Box sx={{ mt: "auto", pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
                    {testimonial.companyUrl ? (
                      <Link
                        href={testimonial.companyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          textDecoration: "none",
                          color: "primary.main",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {testimonial.companyLogo ? (
                          <Box
                            component="img"
                            src={testimonial.companyLogo}
                            alt={`${testimonial.company} logo`}
                            sx={{
                              height: 24,
                              width: "auto",
                              maxWidth: 80,
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <BusinessIcon sx={{ fontSize: 16 }} />
                        )}
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {testimonial.company}
                        </Typography>
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </Link>
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {testimonial.companyLogo ? (
                          <Box
                            component="img"
                            src={testimonial.companyLogo}
                            alt={`${testimonial.company} logo`}
                            sx={{
                              height: 24,
                              width: "auto",
                              maxWidth: 80,
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <Chip
                            icon={<BusinessIcon />}
                            label={testimonial.company}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        )}
                        {testimonial.companyLogo && (
                          <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
                            {testimonial.company}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </Paper>
            </motion.div>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default TestimonialsSection;
